import { doPost } from "../API/apis";

/**
 * Patient Service - Handles all patient-related API operations
 */
class PatientService {
  /**
   * Register a new patient
   * @param {Object} patientData - Patient registration data
   * @returns {Promise<Object>} - API response
   */
  static async registerPatient(patientData) {
    try {
      const response = await doPost("/patient/register/", patientData);
      return {
        success: response?.status === 201,
        data: response?.data,
        status: response?.status
      };
    } catch (error) {
      console.error("Error registering patient:", error);
      throw new Error("Failed to register patient. Please try again.");
    }
  }

  /**
   * Build patient data object from form values and additional data
   * @param {Object} values - Form values
   * @param {Object} conditionalFields - Conditional field states
   * @param {Object} images - Profile and passport images
   * @returns {Object} - Formatted patient data for API
   */
  static buildPatientData(values, conditionalFields, images) {
    return {
      // Basic Information
      same_as_address: !conditionalFields.isSame,
      first_name: values.firstName,
      middle_name: values.middleName,
      last_name: values.lastName,
      date_of_birth: values?.dob,
      gender: values.gender === "Other" ? values.otherGender : values.gender,
      marital_status:
        values.martialStatus === "Other"
          ? values.otherMartialStatus
          : values.martialStatus,
      sexual_orientation:
        values.sexualOrientation === "Other"
          ? values.otherSexualOrientation
          : values.sexualOrientation,
      employment: values.employment_status,
      isMinor: conditionalFields.isMinor,
      basic_information_notes: values.basicNotes,

      // Guardian Information
      guardian_first_name: values.guardian_first_name,
      guardian_middle_name: values.guardian_middle_name,
      guardian_last_name: values.guardian_last_name,
      guardian_phone_number: values.guardianPhone,
      guardian_email: values.guardianEmail,

      // Contact Information
      patient_phone: values.phone,
      phone_decline: false,
      email_decline: false,
      patient_email: values.email,
      patient_home: values.home,
      patient_work: values.work,
      extension: values.extension,
      patient_contact_notes: values.patient_contact_info_notes,

      // Emergency Contact
      emergency_first_name: values.emergencyContactFirstName,
      emergency_last_name: values.emergencyContactLastName,
      emergency_relationship: values.relationship,
      emergency_phone_number: values.emergencyContactPhone,
      emergency_notes: values.emergency_contact_notes,

      // Current Address
      current_address_line_1: values.address1,
      current_address_line_2: values.address2,
      current_city: values.city,
      current_state: values.state,
      current_zipcode: values.zip,
      current_country: values.country,
      current_address_notes: values.current_address_notes,

      // Mailing Address
      mailing_address_line_1: values.mailingAddress1,
      mailing_address_line_2: values.mailingAddress2,
      mailing_city: values.mailingCity,
      mailing_state: values.mailingState,
      mailing_zipcode: values.mailingZip,
      mailing_country: values.mailingCountry,

      // Demographics
      race: values.race || "notAsked",
      ethnicity: values.ethnicity || "notAsked",
      languages: values.languages,
      demographics_notes: values.demographicsNotes,
      religion: values.religion,

      // Referral Information
      referal_full_name: values.referral_Provider_fullName,
      referal_phone: values.referral_Provider_phone,
      referal_speciality: values.referral_Provider_speciality,
      referal_fax: values.referral_Provider_fax,
      referal_email: values.referral_Provider_email,
      referal_organization_name: values.organization_name,
      referal_group_name: values.group,
      wdyhau: values.wdyhau,
      referal_notes: values.referal_notes,

      // Identification
      govId: values.govtID,
      ssn: values.ssn,
      license: values.license,
      passport: values.passportNumber,
      id_notes: values.idNotes,

      // Images
      profile_picture: images.profile,
      identification_picture: images.passport,

      // Payment Services
      payment_services: values.payment_services,
      payment_services_notes: values.payment_services_notes,
      doctor: null,
    };
  }
}

export default PatientService;
